/**
 * QUEM É O TENANT ATIVO — fonte única.
 *
 * O teste `slug === "regula360" || slug === "vitrine-reg"` estava copiado em
 * 13+ lugares (4 deles chamados `ehRegula`, os outros inline ou com outro nome).
 * Cópia espalhada não é só feio: as cópias JÁ DIVERGIRAM.
 *   - `marcaLogin.ts` e `api/lib/convite.ts` acrescentam `slug.includes("regula")`
 *     — mais largo que o resto;
 *   - `Layout.tsx:126` esqueceu `vitrine-reg` numa allowlist, e por isso um item
 *     de menu some no tenant vitrine da Regula.
 *
 * Esta é a versão ESTRITA (só os dois slugs conhecidos), que é o comportamento
 * da maioria das cópias. As duas variantes com `includes` NÃO foram alteradas
 * aqui de propósito: são caminho de marca/login e podem ser folgadas de caso
 * pensado — mexer nelas é decisão do dono, não limpeza de código.
 *
 * `docs/remediation/TEN-001-tenant-hardcode-register.md` já pedia isso como
 * prioridade alta e nunca foi feito.
 */

/** Slugs que são produto Regula360 — o FALLBACK, pra quem ainda não carrega `marca`. */
const SLUGS_REGULA = ["regula360", "vitrine-reg"] as const;

export type Marca = "m5" | "regula";

type EmpresaComMarca = { slug?: string | null; marca?: string | null };

/**
 * 05/09 (raio-x, bloqueio 10): a marca passou a ser DADO (`empresas.marca`, migration
 * 202609051551). O slug segue como fallback pra objetos que ainda não trazem a coluna
 * (payload antigo em cache, testes que só passam o slug). Quando os dois existem, o dado
 * manda — é ele que permite onboardar um cliente novo sem tocar em código.
 */
function marcaDoDado(e: EmpresaComMarca | string | null | undefined): string | null {
  return typeof e === "object" && e ? (e.marca ?? null) : null;
}

/** O tenant é Regula360? Aceita o objeto da empresa ativa (com `marca`) ou o slug cru. */
export function ehRegula(empresaOuSlug: EmpresaComMarca | string | null | undefined): boolean {
  const dado = marcaDoDado(empresaOuSlug);
  if (dado) return dado === "regula";
  const slug = typeof empresaOuSlug === "string" ? empresaOuSlug : (empresaOuSlug?.slug ?? "");
  return (SLUGS_REGULA as readonly string[]).includes(slug.toLowerCase());
}

/**
 * A marca visual do tenant — é ela que escolhe o ACENTO da pele v2
 * (âmbar da M5 × navy da Regula). Tudo que não é Regula usa a marca M5.
 */
export function marcaDoTenant(empresaOuSlug: EmpresaComMarca | string | null | undefined): Marca {
  return ehRegula(empresaOuSlug) ? "regula" : "m5";
}
